import PropTypes from "prop-types";
import React, { useContext } from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AdministrationIcon from "@mui/icons-material/AssignmentInd";
import ContentIcon from "@mui/icons-material/Ballot";
import RepositoryIcon from "@mui/icons-material/Folder";
import SupportIcon from "@mui/icons-material/Help";
import ResourcesIcon from "@mui/icons-material/LocalLibrary";
import ValidateIcon from "@mui/icons-material/CheckCircle";
import PolicyPartnerIcon from "../../components/Icons/PolicyPartnerIcon";
import CommunicationsIcon from "@mui/icons-material/Forum";
import SideNav from "../../components/Nav/SideNav/SideNav";
import {
  dashboard,
  administration,
  courses,
  adminPolicies,
  repositories,
  repositoryIntegrations,
  administrationEmployees,
  administrationLocations,
  administrationPositions,
  administrationSpecialAssignments,
  administrationGroups,
  administrationBulkAssignmentTool,
  validate,
  settings,
  featureSettings,
  communications,
  communicationsAlerts,
  policySettings
} from "../../routes/AdminRoutes";
import { Settings } from "@mui/icons-material";
import AppContext from "../../context/AppContext";

const AdminSideNav = ({
  currentPath,
  onExpandSideNav,
  adminLogoImage,
  onSupportClick,
  expanded,
  isInstructor,
  hasValidateAccess,
  hasPolicyPartnerAdminAccess,
  hasRepositoryAccess,
  hasTopLevelRepositoryNav,
  hasRepositoryIntegration,
  hasAdminDashboardAccess,
  hasAdministrationAccess,
  hasBulkAssignmentToolAccess,
  hasSpecialAssignmentsAccess,
  hasEmailAlertsAccess,
  hasFeatureSettings,
  isPolicyAdmin,
  isAdmin
}) => {
  const dispatch = useDispatch();

  const { mobileNavbarOpen, setMobileNavbarOpen } = useContext(AppContext);

  const handleMenuNavItemClick = itemClicked => {
    if (itemClicked.title === "sideNav.support") {
      onSupportClick();
    } else {
      if (itemClicked.path) {
        dispatch(push(itemClicked.path));
        setMobileNavbarOpen(!mobileNavbarOpen);
      }
    }
    if (itemClicked.title === "sideNav.resources") {
      window.open("https://gracehillvision.com/crc");
    }
  };

  const sideNavRoutes = [
    {
      path: dashboard,
      title: "sideNav.dashboard",
      icon: <DashboardIcon />,
      section: "upper",
      hidden: !hasAdminDashboardAccess
    },
    {
      path: administration,
      title: "sideNav.administration",
      icon: <AdministrationIcon />,
      children: [
        {
          path: administrationEmployees,
          title: "sideNav.employees",
          hidden: false
        },
        {
          path: administrationLocations,
          title: "sideNav.locations",
          hidden: !(isAdmin || isPolicyAdmin)
        },
        {
          path: administrationPositions,
          title: "sideNav.positions",
          hidden: !(isAdmin || isPolicyAdmin)
        },
        {
          path: administrationGroups,
          title: "sideNav.groups",
          hidden: !(isAdmin || isPolicyAdmin)
        },
        {
          path: administrationSpecialAssignments,
          title: "sideNav.specialAssignments",
          hidden: !hasSpecialAssignmentsAccess
        },
        {
          path: administrationBulkAssignmentTool,
          title: "sideNav.bulkAssignmentTool",
          hidden: !hasBulkAssignmentToolAccess
        }
      ],
      section: "upper",
      hidden: !hasAdministrationAccess
    },
    {
      path: courses,
      title: "sideNav.courses",
      section: "upper",
      icon: <ContentIcon />,
      hidden: !hasTopLevelRepositoryNav
    },
    {
      path: adminPolicies,
      title: "sideNav.policies",
      section: "upper",
      icon: <PolicyPartnerIcon />,
      hidden: !hasPolicyPartnerAdminAccess
    },
    {
      path: courses,
      title: "sideNav.vision",
      icon: <ContentIcon />,
      children: [
        {
          path: courses,
          title: "sideNav.courses",
          hidden: false
        },
        {
          path: repositories,
          title: "sideNav.repository",
          hidden: !hasRepositoryAccess
        }
      ],
      expandable: true,
      section: "upper",
      hidden: hasTopLevelRepositoryNav || !(isAdmin || isInstructor)
    },
    {
      path: repositories,
      title: "sideNav.repository",
      icon: <RepositoryIcon />,
      hidden: !hasRepositoryAccess
    },
    {
      path: repositories,
      title: "sideNav.repository",
      icon: <RepositoryIcon />,
      hidden: !hasRepositoryAccess
    },
    {
      path: validate,
      title: "sideNav.validate",
      icon: <ValidateIcon />,
      section: "upper",
      hidden: !hasValidateAccess
    },
    {
      path: repositories,
      title: "sideNav.repository",
      icon: <RepositoryIcon />,
      section: "upper",
      hidden: !hasTopLevelRepositoryNav
    },
    {
      path: settings,
      title: "sideNav.settings",
      icon: <Settings />,
      children: [
        {
          path: featureSettings,
          title: "sideNav.featureSettings",
          hidden: !isAdmin || !hasFeatureSettings
        },
        {
          path: repositoryIntegrations,
          title: "sideNav.integrationSettings",
          hidden: !isAdmin || !hasRepositoryIntegration
        },
        {
          path: policySettings,
          title: "sideNav.policySettings",
          hidden: !hasPolicyPartnerAdminAccess
        }
      ],
      section: "upper",
      hidden:
        (!isAdmin || (!hasFeatureSettings && !hasRepositoryIntegration)) &&
        !isPolicyAdmin
    },
    {
      path: communications,
      title: "sideNav.communications",
      icon: <CommunicationsIcon />,
      children: [
        {
          path: communicationsAlerts,
          title: "sideNav.emailAlerts",
          hidden: !hasEmailAlertsAccess
        }
      ],
      section: "upper",
      hidden: !hasEmailAlertsAccess
    },
    {
      title: "sideNav.resources",
      icon: <ResourcesIcon />,
      section: "lower",
      hidden: !(isAdmin || isPolicyAdmin)
    },
    {
      title: "sideNav.support",
      icon: <SupportIcon />,
      section: "lower",
      hidden: false
    }
  ];

  return (
    <SideNav
      onExpandSideNav={onExpandSideNav}
      expanded={expanded}
      routes={sideNavRoutes}
      mobileNavbarOpen={mobileNavbarOpen}
      logo={adminLogoImage}
      currentPath={currentPath}
      onMenuNavItemClick={handleMenuNavItemClick}
    />
  );
};

export default AdminSideNav;

AdminSideNav.propTypes = {
  adminLogoImage: PropTypes.string,
  currentPath: PropTypes.string,
  expanded: PropTypes.bool,
  onExpandSideNav: PropTypes.func,
  onSupportClick: PropTypes.func,
  isInstructor: PropTypes.bool,
  adminExperience: PropTypes.bool,
  allowValidateRole: PropTypes.bool,
  allowValidate20: PropTypes.bool,
  hasRepositoryAccess: PropTypes.bool,
  hasTopLevelRepositoryNav: PropTypes.bool,
  hasRepositoryIntegration: PropTypes.bool,
  hasEmailAlertsAccess: PropTypes.bool,
  hasFeatureSettings: PropTypes.bool,
  isAdmin: PropTypes.bool,
  allowManageEmployees: PropTypes.bool
};
